// Minimal canvas "screen" base for the menu / result / campaign chrome — its own rAF
// loop, DPR-aware transform, and a click-region system. (The battle has its own richer
// loop in game.ts; this is just for the surrounding screens.)

import { roundRectPath } from "../render/art";

export interface ClickRegion {
  x: number;
  y: number;
  w: number;
  h: number;
  fn: () => void;
  hover?: boolean;
}

export abstract class ScreenBase {
  protected canvas: HTMLCanvasElement;
  protected ctx: CanvasRenderingContext2D;
  protected vw = 0;
  protected vh = 0;
  protected dpr = 1;
  protected regions: ClickRegion[] = [];
  protected mouse = { x: 0, y: 0 };
  private raf = 0;
  private running = false;
  private time = 0;
  private last = 0;
  private listeners: Array<[EventTarget, string, EventListener]> = [];

  constructor(canvas: HTMLCanvasElement, ctx: CanvasRenderingContext2D) {
    this.canvas = canvas;
    this.ctx = ctx;
  }

  mount(): void {
    this.resize();
    this.add(window, "resize", () => this.resize());
    this.add(this.canvas, "mousemove", (e) => {
      const r = this.canvas.getBoundingClientRect();
      this.mouse = { x: (e as MouseEvent).clientX - r.left, y: (e as MouseEvent).clientY - r.top };
    });
    this.add(this.canvas, "click", (e) => {
      const r = this.canvas.getBoundingClientRect();
      const x = (e as MouseEvent).clientX - r.left;
      const y = (e as MouseEvent).clientY - r.top;
      for (const reg of this.regions) if (x >= reg.x && x <= reg.x + reg.w && y >= reg.y && y <= reg.y + reg.h) {
        reg.fn();
        return;
      }
    });
    this.running = true;
    this.last = performance.now();
    const frame = () => {
      if (!this.running) return;
      const now = performance.now();
      const dt = (now - this.last) / 1000;
      this.last = now;
      this.time += dt;
      this.regions = [];
      this.ctx.setTransform(this.dpr, 0, 0, this.dpr, 0, 0);
      this.draw(this.time);
      this.raf = requestAnimationFrame(frame);
    };
    this.raf = requestAnimationFrame(frame);
  }

  unmount(): void {
    this.running = false;
    cancelAnimationFrame(this.raf);
    for (const [t, ev, l] of this.listeners) t.removeEventListener(ev, l);
    this.listeners = [];
  }

  private add(t: EventTarget, ev: string, fn: EventListener): void {
    t.addEventListener(ev, fn);
    this.listeners.push([t, ev, fn]);
  }

  protected resize(): void {
    this.dpr = Math.min(2, window.devicePixelRatio || 1);
    this.vw = window.innerWidth;
    this.vh = window.innerHeight;
    this.canvas.width = Math.floor(this.vw * this.dpr);
    this.canvas.height = Math.floor(this.vh * this.dpr);
    this.canvas.style.width = `${this.vw}px`;
    this.canvas.style.height = `${this.vh}px`;
  }

  protected button(x: number, y: number, w: number, h: number, label: string, fn: () => void, opts?: { primary?: boolean; color?: string }): void {
    const ctx = this.ctx;
    const hover = this.mouse.x >= x && this.mouse.x <= x + w && this.mouse.y >= y && this.mouse.y <= y + h;
    const base = opts?.color ?? (opts?.primary ? "#8a1f2b" : "#2a2620");
    ctx.fillStyle = hover ? lighten(base) : base;
    roundRectPath(ctx, x, y, w, h, 6);
    ctx.fill();
    ctx.strokeStyle = hover ? "#e8e0d4" : "rgba(150,140,120,0.6)";
    ctx.lineWidth = 1.5;
    ctx.stroke();
    ctx.fillStyle = "#f0e8dc";
    ctx.font = "700 16px Trebuchet MS";
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText(label, x + w / 2, y + h / 2);
    ctx.textAlign = "left";
    ctx.textBaseline = "alphabetic";
    this.regions.push({ x, y, w, h, fn });
    if (hover) this.canvas.style.cursor = "pointer";
  }

  protected clearCursor(): void {
    this.canvas.style.cursor = "default";
  }

  abstract draw(time: number): void;
}

function lighten(hex: string): string {
  const n = parseInt(hex.slice(1), 16);
  const r = Math.min(255, ((n >> 16) & 255) + 34);
  const g = Math.min(255, ((n >> 8) & 255) + 34);
  const b = Math.min(255, (n & 255) + 34);
  return `rgb(${r},${g},${b})`;
}
